import React from 'react';
import './FmKeyFigures.css';

export type FmKeyFigureTone = 'neutral' | 'positive' | 'warning' | 'danger';
export type FmKeyFigureDeltaDirection = 'up' | 'down' | 'flat';

export interface FmKeyFigureDelta {
  label: React.ReactNode;
  direction: FmKeyFigureDeltaDirection;
  /** Also mark the value with a direction glyph. */
  marker?: boolean;
}

interface FmKeyFigureBase {
  /** Stable identity within the list. */
  id: string;
  label: React.ReactNode;
  unit?: React.ReactNode;
  footnote?: React.ReactNode;
  tone?: FmKeyFigureTone;
  delta?: FmKeyFigureDelta;
  testId?: string;
  valueTestId?: string;
}

interface FmAvailableKeyFigure extends FmKeyFigureBase {
  value: Exclude<React.ReactNode, null | undefined>;
  unavailableReason?: never;
}

interface FmUnavailableKeyFigure extends FmKeyFigureBase {
  /** An explicit missing value: the slot shows an em dash and the reason below it. */
  value: null;
  /** Translated reason the figure is missing. */
  unavailableReason: React.ReactNode;
}

/** One key figure: value, unit, label, footnote, tone, or an explicit unavailable with its reason. */
export type FmKeyFigure = FmAvailableKeyFigure | FmUnavailableKeyFigure;

interface FmKeyFiguresBaseProps {
  items: FmKeyFigure[];
  /**
   * @internal DS-internal className prop; forbidden in route-owned code.
   */
  className?: string;
  'data-testid'?: string;
}

export interface FmKeyFiguresTilesProps extends FmKeyFiguresBaseProps {
  /** Separate tiles that wrap by container width. */
  presentation?: 'tiles';
  /** `card` keeps individual tiles; `chart` draws one figure-surface panel with unboxed cells. */
  surface?: 'card' | 'chart';
}

export interface FmKeyFiguresRailProps extends FmKeyFiguresBaseProps {
  /** Unboxed figures divided by hairlines, for copy rails and figure pairs. */
  presentation: 'rail';
  columns?: 2 | 3 | 4;
  /** `responsive` wraps by the rail's own container width instead of a fixed column count. */
  layout?: 'fixed' | 'responsive';
  /**
   * `display` is the stat band: each value at the statement tier with its
   * label read as the sentence under it, no dividers. It sets its own
   * responsive columns, so `columns` and `layout` do not apply.
   */
  scale?: 'standard' | 'display';
}

export type FmKeyFiguresProps = FmKeyFiguresTilesProps | FmKeyFiguresRailProps;

const cx = (...parts: Array<string | false | null | undefined>): string =>
  parts.filter(Boolean).join(' ');

const DIRECTION_GLYPH: Record<FmKeyFigureDeltaDirection, string> = {
  up: '▲',
  down: '▼',
  flat: '◆',
};

/**
 * The value and its unit are one reading: they sit in one unbreakable run so
 * "214 000" never ends a line with "€" starting the next. An unavailable figure
 * prints an em dash and its reason as one body-size line in the value slot: a
 * dash at headline size reads as a grey bar.
 */
const KeyFigure: React.FC<{ item: FmKeyFigure }> = ({ item }) => {
  const unavailable = item.value === null;
  return (
    <div
      className={cx(
        'fm-key-figures__item',
        item.tone && item.tone !== 'neutral' && `fm-key-figures__item--${item.tone}`,
      )}
      data-testid={item.testId}
      data-unavailable={unavailable ? 'true' : undefined}
    >
      <span className="fm-key-figures__label">{item.label}</span>
      {unavailable ? (
        <span className="fm-key-figures__value" data-testid={item.valueTestId}>
          <span aria-hidden="true">—</span>
          <span className="fm-key-figures__reason">{item.unavailableReason}</span>
        </span>
      ) : (
        <span className="fm-key-figures__value" data-testid={item.valueTestId}>
          {item.value}
          {item.unit ? <span className="fm-key-figures__unit">{item.unit}</span> : null}
          {item.delta?.marker ? (
            <span className="fm-key-figures__marker" aria-hidden="true">
              {DIRECTION_GLYPH[item.delta.direction]}
            </span>
          ) : null}
        </span>
      )}
      {!unavailable && item.delta ? (
        <span
          className={cx('fm-key-figures__delta', `fm-key-figures__delta--${item.delta.direction}`)}
        >
          {item.delta.label}
        </span>
      ) : null}
      {item.footnote ? <span className="fm-key-figures__footnote">{item.footnote}</span> : null}
    </div>
  );
};

/** Column count follows the figure count so a last row never holds a lone orphan. */
function responsiveCount(count: number): string {
  if (count <= 4) return String(count);
  return count % 3 === 0 || count === 5 ? 'three' : 'four';
}

function railClasses(props: FmKeyFiguresRailProps): Array<string | false> {
  if (props.scale === 'display') return ['fm-key-figures--rail', 'fm-key-figures--display'];
  return [
    'fm-key-figures--rail',
    props.layout === 'responsive'
      ? 'fm-key-figures--responsive'
      : `fm-key-figures--columns-${props.columns ?? 4}`,
  ];
}

/**
 * FmKeyFigures — the one key-figures component. Every figure carries a unit
 * slot and an explicit unavailable state, so a missing number reads the same
 * way on every surface.
 */
export const FmKeyFigures: React.FC<FmKeyFiguresProps> = (props) => {
  const { items, className, 'data-testid': dataTestId } = props;
  const presentationClasses =
    props.presentation === 'rail'
      ? railClasses(props)
      : ['fm-key-figures--tiles', props.surface === 'chart' && 'fm-key-figures--chart-surface'];
  return (
    <div
      className={cx('fm-key-figures', ...presentationClasses, className)}
      data-testid={dataTestId}
    >
      <div className="fm-key-figures__grid" data-count={responsiveCount(items.length)}>
        {items.map((item) => (
          <KeyFigure key={item.id} item={item} />
        ))}
      </div>
    </div>
  );
};

FmKeyFigures.displayName = 'FmKeyFigures';
